import { formatVariantLabel } from 'lib/format-variant';

export function normalizeRoomCode(code: string): string {
  return String(code || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/** Lobby deep link — opens join tab with code prefilled (same variant). */
export function buildPrivateRoomInviteUrl(roomCode: string, variantCode?: string): string {
  const code = normalizeRoomCode(roomCode);
  const variant = encodeURIComponent(formatVariantLabel(variantCode || 'Classic'));
  const params = new URLSearchParams({ variant, roomCode: code, private: '1' });
  return `${window.location.origin}/lobby?${params.toString()}`;
}

export function buildPrivateRoomInviteMessage(
  roomCode: string,
  variantCode?: string,
  tableName?: string
): string {
  const code = normalizeRoomCode(roomCode);
  const variant = formatVariantLabel(variantCode || 'Classic');
  const label = tableName?.trim() || `${variant} private table`;
  const link = buildPrivateRoomInviteUrl(code, variantCode);
  return `Join my Teen Patti table "${label}" (${variant}). Room code: ${code}\n${link}`;
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export async function copyPrivateRoomCode(roomCode: string): Promise<boolean> {
  return copyTextToClipboard(normalizeRoomCode(roomCode));
}

export async function sharePrivateRoomInvite(params: {
  roomCode: string;
  variantCode?: string;
  tableName?: string;
}): Promise<'shared' | 'copied' | 'failed'> {
  const message = buildPrivateRoomInviteMessage(params.roomCode, params.variantCode, params.tableName);
  const url = buildPrivateRoomInviteUrl(params.roomCode, params.variantCode);
  const title = 'Teen Patti — private room invite';

  try {
    if (navigator.share) {
      await navigator.share({ title, text: message, url });
      return 'shared';
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return 'failed';
    }
  }

  const copied = await copyTextToClipboard(message);
  return copied ? 'copied' : 'failed';
}
